import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { router } from './App';
import { AuthGate } from './components/auth/AuthGate';
import { ErrorBoundary } from './components/ErrorBoundary';
import { installAuthFetch } from './lib/authBridge';
import { initObservability } from './lib/observability';
import { installClientErrorReporting } from './lib/error-reporter';
import { useAuthStore } from './lib/authStore'; // registers the 401 → lock handler + cross-tab sync
import './index.css';
import './responsive.css';

// ─── User Guides self-host bootstrap (record / pick / probe / preview over the LIVE PCP DOM) ─
// When the User Guides designer pops THIS app open as the recorder / picker /
// probe / preview target (same-origin self-host), it appends `?pcpGuidesRecord=<token>`,
// `?pcpGuidesPick=1`, `?pcpGuidesProbe=1`, or `?pcpGuidesPreview=<guideId>` (+ `pgKey`/
// `pcpGuidesKey`/`pgSession`). The vanilla-JS player bundle reads those query params
// directly, so all we do here is inject the player <script> with the app key (from
// `pgKey`, so the player loads the EDITED app's manifest — not this origin's
// self-embed key). Tiny + guarded: no effect on a normal load (those params are
// absent — bootstrapGuidesEmbed handles the normal case), and it runs once.
function bootstrapGuidesRecorder(): void {
  try {
    const q = new URLSearchParams(window.location.search);
    const recordToken = q.get('pcpGuidesRecord');
    const pickMode = q.get('pcpGuidesPick') === '1' || q.get('pcpGuidesPick') === 'true';
    const probeMode = q.get('pcpGuidesProbe') === '1' || q.get('pcpGuidesProbe') === 'true';
    const previewId = q.get('pcpGuidesPreview');
    if (!recordToken && !pickMode && !probeMode && !previewId) return; // normal load — no-op

    // Don't double-inject (StrictMode / HMR re-entry).
    if (document.querySelector('script[data-pcp-guides-bootstrap]')) return;

    // record/pick pass the key as `pgKey`; the Preview button passes `pcpGuidesKey`.
    const appKey = q.get('pgKey') ?? q.get('pcpGuidesKey') ?? '';
    const s = document.createElement('script');
    s.src = '/api/guides/v1/pcp-guides.js';
    if (appKey) s.setAttribute('data-app-key', appKey);
    s.setAttribute('data-pcp-guides-bootstrap', '1');
    // The player resolves its mode from the query params (record token / pick flag)
    // and the cross-window session from `pgSession`; mirror the record token onto
    // the hash too so getRecordToken()'s legacy-hash path also matches.
    if (recordToken && !/[#&]pcp-guides-record=/.test(window.location.hash)) {
      try {
        window.location.hash = `#pcp-guides-record=${recordToken}`;
      } catch {
        /* ignore — the ?pcpGuidesRecord= query alone is sufficient */
      }
    }
    document.head.appendChild(s);
  } catch {
    /* never block app startup on the guides bootstrap */
  }
}

bootstrapGuidesRecorder();

// ─── User Guides self-embed (PUBLISHED guides during normal PCP use) ───────────
// On a NORMAL load (no record/pick/preview params), ask the API whether THIS origin
// is a registered guides app; if so, inject the player with that app's public key so
// published, URL-matching guides auto-play during regular PCP use. The /self-embed
// endpoint requires a session (the login page never loads the player) and returns
// null for an origin with no registered app. Fire-and-forget; never blocks startup.
// Tell the self-embedded player who the signed-in PCP user is, so templated
// Smartguide content (f-00187: {{ user.name }} / {{ user.email }}) resolves. The
// auth user hydrates async (AuthGate), so we re-identify whenever it changes;
// `lastGuidesIdentity` debounces redundant calls. No-op until the player global
// exists and the user is known.
let lastGuidesIdentity = '';
function identifyGuidesViewer(): void {
  try {
    const api = (window as unknown as { PCPGuides?: { identify?: (u: Record<string, unknown>) => void } }).PCPGuides;
    if (!api || typeof api.identify !== 'function') return;
    const user = useAuthStore.getState().user;
    if (!user) return;
    const roles = user.roles && user.roles.length ? user.roles : undefined;
    const key = `${user.id}|${user.displayName ?? ''}|${user.email ?? ''}|${roles?.join(',') ?? ''}`;
    if (key === lastGuidesIdentity) return;
    lastGuidesIdentity = key;
    api.identify({
      userId: user.id,
      userName: user.displayName ?? undefined,
      userEmail: user.email ?? undefined,
      // Extra identity for {{ user.* }} templating (f-00203): the viewer's role name(s).
      roles,
      role: roles?.[0],
    });
  } catch {
    /* never block on identify */
  }
}

function bootstrapGuidesEmbed(): void {
  try {
    const q = new URLSearchParams(window.location.search);
    // A mode bootstrap (record/pick/probe/preview) already owns the player on those loads.
    if (q.get('pcpGuidesRecord') || q.get('pcpGuidesPick') || q.get('pcpGuidesProbe') || q.get('pcpGuidesPreview')) return;
    if (document.querySelector('script[data-pcp-guides-bootstrap]')) return;
    void fetch(`/api/guides/v1/self-embed?origin=${encodeURIComponent(window.location.origin)}`, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        const key = body?.data?.publicKey;
        if (!key || document.querySelector('script[data-pcp-guides-bootstrap]')) return;
        const s = document.createElement('script');
        s.src = '/api/guides/v1/pcp-guides.js';
        s.setAttribute('data-app-key', key);
        s.setAttribute('data-pcp-guides-bootstrap', '1');
        s.addEventListener('load', () => {
          identifyGuidesViewer();
          // Re-identify once the auth user hydrates / changes.
          useAuthStore.subscribe(identifyGuidesViewer);
        });
        document.head.appendChild(s);
      })
      .catch(() => { /* offline / 401 / no app for this origin — no player */ });
  } catch {
    /* never block app startup on the guides embed */
  }
}

bootstrapGuidesEmbed();

// Wrap window.fetch once so any /api 401 (session lapsed) triggers the lock.
installAuthFetch();

// Always-on client error capture → server app_errors store. Independent of the
// telemetry gate (so it works in local dev, where OpenObserve/OTel are off) and
// readable by Claude via the MCP tools + the Diagnostics page.
installClientErrorReporting();

// Fire-and-forget client telemetry (OpenObserve RUM + replay). No-op unless the
// server's runtime-config enables it; never throws into startup.
void initObservability();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element not found');
}

createRoot(rootElement).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthGate>
          <RouterProvider router={router} />
        </AuthGate>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
